import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import type {
  NotificationBulkUpdateResponse,
  NotificationRecord,
  NotificationRecordListResponse,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

interface NotificationQueryOptions {
  category?: string;
  status?: string;
  skip?: number;
  limit?: number;
  enabled?: boolean;
}

const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || 'An error occurred';
    }
  }
  return 'An error occurred';
};

const buildQueryString = (options: NotificationQueryOptions) => {
  const params = new URLSearchParams();
  if (options.category) {
    params.append('category', options.category);
  }
  if (options.status) {
    params.append('status', options.status);
  }
  params.append('skip', String(options.skip ?? 0));
  params.append('limit', String(options.limit ?? 50));
  return params.toString();
};

export const useNotifications = (options: NotificationQueryOptions = {}) => {
  const { category, status, skip = 0, limit = 50, enabled = true } = options;

  return useQuery({
    queryKey: ['notifications', { category, status, skip, limit }],
    queryFn: async () => {
      const { data } = await apiClient.get<NotificationRecordListResponse>(
        `/notifications?${buildQueryString({ category, status, skip, limit })}`
      );
      return data;
    },
    enabled,
    refetchInterval: 1000 * 60,
  });
};

export const useUnreadNotificationCount = (enabled = true) => {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await apiClient.get<NotificationRecordListResponse>(
        `/notifications?${buildQueryString({ status: 'unread', skip: 0, limit: 1 })}`
      );
      return data.total;
    },
    enabled,
    refetchInterval: 1000 * 60,
  });
};

const invalidateNotificationQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['notifications'] });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data } = await apiClient.post<NotificationRecord>(`/notifications/${notificationId}/read`);
      return data;
    },
    onSuccess: () => {
      invalidateNotificationQueries(queryClient);
    },
  });
};

export const useDismissNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data } = await apiClient.post<NotificationRecord>(`/notifications/${notificationId}/dismiss`);
      return data;
    },
    onSuccess: () => {
      invalidateNotificationQueries(queryClient);
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<NotificationBulkUpdateResponse>('/notifications/read-all');
      return data;
    },
    onSuccess: (data) => {
      invalidateNotificationQueries(queryClient);
      addNotification({
        message: data.updated_count > 0
          ? `Marked ${data.updated_count} notification${data.updated_count === 1 ? '' : 's'} as read`
          : 'No unread notifications to update',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addNotification({
        message: extractErrorMessage(error) || 'Failed to update notifications',
        type: 'error',
      });
    },
  });
};
